import { BUILDER_DECIDERS, BUILDER_GATE_APPROVERS, BUILDER_WHO, OWNER_OF_WHO, type BuilderWho, type PhaseKey, type TaskOwner } from '@nsb/core';
import { stepName, type TilePhase, type TileStep } from './model.ts';

/**
 * What the card of a step of the job's own asks and sends (design study 12, own step A; OwnStepCard): who does each kind,
 * decides it or approves it, in the card's words; the step it adds, whose by the one rule the lock-in reads; and what it
 * can come after.
 */

/** The kinds the tools add, as the job stores them. */
export type OwnKind = 'task' | 'decision' | 'gate' | 'milestone';
const STEP_KIND = { task: 'work', decision: 'decision', gate: 'gate', milestone: 'milestone' } as const;

/** What is asked of each kind, in the card's words: who does it, decides it or approves it; a milestone is nobody's. */
export const OWN_ASKS: Record<OwnKind, { title: string; options: readonly BuilderWho[]; first: BuilderWho | null } | null> = {
  task: { title: 'Who does it', options: BUILDER_WHO, first: 'nsb' },
  decision: { title: 'Who decides', options: BUILDER_DECIDERS, first: 'client' },
  // How it opens, as the master's gates: the client, a sub or NSB approves it; nobody chosen, and it happens.
  gate: { title: 'Who approves it', options: BUILDER_GATE_APPROVERS, first: null },
  milestone: null,
};
export const OWN_WHO_WORDS: Record<BuilderWho, string> = { nsb: 'NSB', sub: 'A sub', supplier: 'A supplier', client: 'The client', outside: 'Outside' };

/** The kind in words on the card's head; a gate has none, only its diamond (Jason, 2026-10-06). */
export const OWN_KIND_WORDS: Record<OwnKind, string> = { task: 'Work', decision: 'Decision', gate: '', milestone: 'Milestone' };

/** What the card sends: the step of the job's own, as `POST /process/steps` takes it. */
export interface OwnStepBody {
  title: string;
  kind: (typeof STEP_KIND)[OwnKind];
  phase: PhaseKey | null;
  owner: TaskOwner | null;
  requires: string[];
}

/** The card's choices as the step it adds: whose it is by the one rule the lock-in reads (core `OWNER_OF_WHO`). */
export function ownStepBody(kind: OwnKind, phase: string | null, choice: { title: string; who: BuilderWho | null; after: string | null }): OwnStepBody {
  return {
    title: choice.title.trim(),
    kind: STEP_KIND[kind],
    phase: phase as PhaseKey | null,
    owner: OWN_ASKS[kind] && choice.who ? OWNER_OF_WHO[choice.who] : null,
    requires: choice.after ? [choice.after] : [],
  };
}

/** What a step of the job's own can come after: the job's steps (never what is only offered, a dot, or the end), by phase. */
export function afterChoices(phases: readonly TilePhase[], steps: readonly TileStep[]): { name: string; steps: { key: string; title: string }[] }[] {
  const on = steps.filter((s) => !s.ghost && !s.dot && s.end !== 'end');
  const pick = (f: (s: TileStep) => boolean) => on.filter(f).map((s) => ({ key: s.key, title: stepName(s.title) }));
  const known = new Set(phases.map((p) => p.key));
  return [
    { name: 'The start', steps: pick((s) => s.end === 'start') },
    ...phases.map((p) => ({ name: p.name, steps: pick((s) => s.phase === p.key && !s.end) })),
    { name: 'In no phase', steps: pick((s) => !s.end && (s.phase === null || !known.has(s.phase))) },
  ].filter((g) => g.steps.length);
}
